import { GAMES_DATA_URL, fetchGamesData, filterGames, sportDefaultValues, sportLimits } from '@/api/gamesService';
import { getEligibleRecapUrl } from '@/lib/recap';
import { getAllPosts } from '@/utils/blog';
import { getCompetitionUrlSlug, mapUrlParamToCompetition } from '@/utils/sportUtils';
import type { FilterOptions, Game, SoccerCompetition, SportType } from '@/types';

type JsonRecord = Record<string, unknown>;

interface WebMcpTool {
  name: string;
  description: string;
  inputSchema: JsonRecord;
  execute: (input?: JsonRecord) => Promise<unknown> | unknown;
}

interface ModelContextProvider {
  provideContext: (context: { tools: WebMcpTool[] }) => void;
}

type NavigatorWithModelContext = Navigator & {
  modelContext?: ModelContextProvider;
};

declare global {
  interface Window {
    __skoreWebMcpRegistered?: boolean;
  }
}

const sportLabels: Record<SportType, string> = {
  nba: 'NBA',
  soccer: 'Soccer',
  nfl: 'NFL',
  mlb: 'MLB',
  nhl: 'NHL',
};

const sportDescriptions: Record<SportType, string> = {
  nba: 'Spoiler-free NBA games filtered by closeness.',
  soccer: 'Spoiler-free soccer matches filtered by combined goals and competition.',
  nfl: 'Spoiler-free NFL games filtered by closeness.',
  mlb: 'Spoiler-free MLB games filtered by closeness.',
  nhl: 'Spoiler-free NHL games filtered by combined goals.',
};

const supportedSports = Object.keys(sportLabels) as SportType[];

function asNavigatorWithModelContext(): NavigatorWithModelContext {
  return navigator as NavigatorWithModelContext;
}

function normalizeSport(value: unknown): SportType | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.toLowerCase();
  return supportedSports.includes(normalized as SportType) ? (normalized as SportType) : null;
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function normalizeLimit(value: unknown, fallback: number, upperBound: number): number {
  const parsed = normalizeNumber(value);
  if (parsed === undefined) {
    return fallback;
  }

  return Math.min(Math.max(parsed, 0), upperBound);
}

function normalizeResultLimit(value: unknown, fallback = 10): number {
  const parsed = normalizeNumber(value);
  if (parsed === undefined) {
    return fallback;
  }

  return Math.min(Math.max(Math.floor(parsed), 1), 25);
}

function normalizeCompetitions(value: unknown): SoccerCompetition[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const normalized = value
    .map((item) => {
      if (typeof item !== 'string') {
        return null;
      }

      const trimmed = item.trim();
      if (!trimmed) {
        return null;
      }

      const competition = mapUrlParamToCompetition(trimmed);
      return competition === 'All' ? null : competition;
    })
    .filter((competition): competition is SoccerCompetition => competition !== null);

  return normalized.length > 0 ? Array.from(new Set(normalized)) : undefined;
}

function buildSportUrl(
  sport: SportType,
  maxValue?: number,
  minValue?: number,
  competitions?: SoccerCompetition[],
): string {
  const params = new URLSearchParams();
  const defaultMax = sportDefaultValues[sport].default;

  if (typeof maxValue === 'number' && maxValue !== defaultMax) {
    params.set('max', String(maxValue));
  }

  if (typeof minValue === 'number') {
    params.set('min', String(minValue));
  }

  if (sport === 'soccer' && competitions && competitions.length > 0) {
    params.set(
      'competitions',
      competitions
        .map((competition) => getCompetitionUrlSlug(competition))
        .join(','),
    );
  }

  const queryString = params.toString();
  return `/sport/${sport}${queryString ? `?${queryString}` : ''}`;
}

function formatGame(game: Game): JsonRecord {
  return {
    id: game.id,
    title: `${game.awayTeam.name} at ${game.homeTeam.name}`,
    homeTeam: game.homeTeam.name,
    awayTeam: game.awayTeam.name,
    homeAbbreviation: game.homeTeam.abbreviation,
    awayAbbreviation: game.awayTeam.abbreviation,
    competition: game.competition ?? null,
    date: game.date,
    utcDatetime: game.utcDatetime,
    status: game.status.type,
    statusDetail: game.status.detail,
    excitementScore: game.excitementScore ?? null,
    recapUrl: getEligibleRecapUrl(game),
  };
}

function getBlogPostUrl(dateString: string, slug: string): string {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `/blog/${year}/${month}/${day}/${slug}`;
}

function resolvePublicUrl(url: string): string {
  return new URL(url, window.location.origin).toString();
}

function getTools(): WebMcpTool[] {
  return [
    {
      name: 'list-available-sports',
      description: 'List the supported sports and the route each sport page uses on skore.info.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
      execute: () => ({
        sports: supportedSports.map((sport) => ({
          slug: sport,
          name: sportLabels[sport],
          description: sportDescriptions[sport],
          url: `${window.location.origin}/sport/${sport}`,
          defaultMaxValue: sportDefaultValues[sport].default,
          maxValueLimit: sportLimits[sport].max,
        })),
      }),
    },
    {
      name: 'find-spoiler-free-games',
      description: 'Return spoiler-free game matches from the public games feed using the same sport filters the site applies.',
      inputSchema: {
        type: 'object',
        properties: {
          sport: {
            type: 'string',
            enum: supportedSports,
            description: 'One of nba, soccer, nfl, mlb, or nhl.',
          },
          maxValue: {
            type: 'number',
            description: 'For nba, nfl, and mlb this is the maximum point difference. For soccer and nhl this is the minimum combined goals or points threshold.',
          },
          minValue: {
            type: 'number',
            description: 'Optional lower bound used by soccer and nhl filters.',
          },
          competitions: {
            type: 'array',
            description: 'Optional soccer competition names or slugs.',
            items: {
              type: 'string',
            },
          },
          limit: {
            type: 'integer',
            description: 'Maximum number of results to return, between 1 and 25.',
          },
        },
        required: ['sport'],
      },
      execute: async (input = {}) => {
        const sport = normalizeSport(input.sport);

        if (!sport) {
          return {
            error: 'sport must be one of nba, soccer, nfl, mlb, or nhl.',
          };
        }

        const gamesData = await fetchGamesData();
        if (!gamesData?.games) {
          return {
            error: 'The public games feed is currently unavailable.',
          };
        }

        const maxValue = normalizeLimit(input.maxValue, sportDefaultValues[sport].default, sportLimits[sport].max);
        const minValue = sport === 'soccer' || sport === 'nhl' ? normalizeNumber(input.minValue) : undefined;
        const competitions = sport === 'soccer' ? normalizeCompetitions(input.competitions) : undefined;
        const limit = normalizeResultLimit(input.limit);

        const filters: FilterOptions = {
          sportType: sport,
          maxValue,
          minValue,
          competitions,
        };

        const filteredGames = filterGames(gamesData.games, filters)
          .filter((game) => game.status.type === 'completed')
          .sort((left, right) => {
            const excitementDelta = (right.excitementScore ?? -1) - (left.excitementScore ?? -1);
            if (excitementDelta !== 0) {
              return excitementDelta;
            }

            return right.utcDatetime.localeCompare(left.utcDatetime);
          });

        return {
          sport,
          sportName: sportLabels[sport],
          route: `${window.location.origin}${buildSportUrl(sport, maxValue, minValue, competitions)}`,
          appliedFilters: {
            maxValue,
            minValue: minValue ?? null,
            competitions: competitions ?? [],
          },
          totalMatches: filteredGames.length,
          results: filteredGames.slice(0, limit).map((game) => formatGame(game)),
        };
      },
    },
    {
      name: 'list-blog-posts',
      description: 'Return recent skore.info blog posts with dates, tags, and URLs.',
      inputSchema: {
        type: 'object',
        properties: {
          limit: {
            type: 'integer',
            description: 'Maximum number of blog posts to return, between 1 and 25.',
          },
          tag: {
            type: 'string',
            description: 'Optional tag filter, matched case-insensitively.',
          },
        },
      },
      execute: async (input = {}) => {
        const posts = await getAllPosts();
        const limit = normalizeResultLimit(input.limit, 5);
        const tagFilter = typeof input.tag === 'string' ? input.tag.trim().toLowerCase() : '';

        const filteredPosts = posts
          .filter((post) => {
            if (!tagFilter) {
              return true;
            }

            return post.tags.some((tag) => tag.toLowerCase() === tagFilter);
          })
          .sort((left, right) => right.date.localeCompare(left.date));

        return {
          totalMatches: filteredPosts.length,
          results: filteredPosts.slice(0, limit).map((post) => ({
            title: post.title,
            description: post.description,
            date: post.date,
            tags: post.tags,
            readingTime: post.readingTime.text,
            url: `${window.location.origin}${getBlogPostUrl(post.date, post.slug)}`,
          })),
        };
      },
    },
    {
      name: 'open-sport-page',
      description: 'Navigate the current browser tab to one of the sport pages using skore.info filter semantics.',
      inputSchema: {
        type: 'object',
        properties: {
          sport: {
            type: 'string',
            enum: supportedSports,
            description: 'One of nba, soccer, nfl, mlb, or nhl.',
          },
          maxValue: {
            type: 'number',
          },
          minValue: {
            type: 'number',
          },
          competitions: {
            type: 'array',
            items: {
              type: 'string',
            },
          },
        },
        required: ['sport'],
      },
      execute: (input = {}) => {
        const sport = normalizeSport(input.sport);

        if (!sport) {
          return {
            error: 'sport must be one of nba, soccer, nfl, mlb, or nhl.',
          };
        }

        const maxValue = normalizeLimit(input.maxValue, sportDefaultValues[sport].default, sportLimits[sport].max);
        const minValue = sport === 'soccer' || sport === 'nhl' ? normalizeNumber(input.minValue) : undefined;
        const competitions = sport === 'soccer' ? normalizeCompetitions(input.competitions) : undefined;
        const targetPath = buildSportUrl(sport, maxValue, minValue, competitions);

        window.history.pushState({}, '', targetPath);
        window.dispatchEvent(new PopStateEvent('popstate'));
        window.scrollTo({ top: 0, behavior: 'auto' });

        return {
          navigatedTo: `${window.location.origin}${targetPath}`,
        };
      },
    },
    {
      name: 'get-agent-resources',
      description: 'Return the machine-readable discovery resources exposed by skore.info.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
      execute: () => ({
        apiCatalog: `${window.location.origin}/.well-known/api-catalog`,
        openapi: `${window.location.origin}/.well-known/openapi.json`,
        skillsIndex: `${window.location.origin}/.well-known/agent-skills/index.json`,
        llms: `${window.location.origin}/llms.txt`,
        llmsFull: `${window.location.origin}/llms-full.txt`,
        gamesFeed: resolvePublicUrl(GAMES_DATA_URL),
        blogFeed: `${window.location.origin}/api/blog/posts.json`,
        status: `${window.location.origin}/api/status.json`,
        scope: 'public-read-only-discovery',
        intentionallyUnsupported: [
          'OAuth/OIDC discovery',
          'OAuth Protected Resource Metadata',
          'A2A Agent Card',
          'commerce/payment metadata',
        ],
      }),
    },
  ];
}

function registerOnce(): void {
  if (window.__skoreWebMcpRegistered) {
    return;
  }

  const provider = asNavigatorWithModelContext().modelContext;
  if (!provider?.provideContext) {
    return;
  }

  provider.provideContext({
    tools: getTools(),
  });

  window.__skoreWebMcpRegistered = true;
}

export function registerWebMcp(attempt = 0): void {
  registerOnce();

  if (window.__skoreWebMcpRegistered || attempt >= 10) {
    return;
  }

  window.setTimeout(() => {
    registerWebMcp(attempt + 1);
  }, 300);
}
